import { AccountStore } from 'base/account_store';
import { AnalyticsService } from 'base/analytics';
import { pluralise } from 'base/helpers';
import { navigateInternal } from 'base/navigation';
import { Banner } from 'base/ui/banner';
import { Button } from 'base/ui/button';
import { Icon } from 'base/ui/icon';
import { IconClockGradient, IconProjectCreateGradient } from 'base/ui/icons';
import { Inline } from 'base/ui/layout';
import { TextSmall, TextSmallLight } from 'base/ui/typography';
import { useToggle } from 'base/useToggle';
import { ProFeatureGateDialog } from 'blocks/billing/feature_gate_dialog';
import { getHumanPlanName } from 'blocks/billing/plan_utils';
import { ProjectsDataStore } from 'blocks/dashboard/project_data/projects_data_store';
import { manageAccountRouteToUrl } from 'blocks/manage_account/routes';
import { observer } from 'mobx-react-lite';
import React from 'react';

export const UpgradeBanner = observer(function _UpgradeBanner({
  accountStore,
  projectsListStore,
  analytics,
}: {
  accountStore: AccountStore;
  projectsListStore: ProjectsDataStore;
  analytics: AnalyticsService;
}) {
  const {
    toggleOn: openUpgradeDialog,
    toggleOff: closeUpgradeDialog,
    value: upgradeDialogOpen,
  } = useToggle();

  const me = accountStore.member;
  if (projectsListStore.state.type != 'loaded' || me == null) {
    return null;
  }

  const { planName, trialDaysRemaining } = me;

  const remainingProjects = projectsListStore.state.res.projectsQuota.limit
    ? // It's possible for `used` to be over `limit` if user has downgraded from a paid plan.
      Math.max(
        projectsListStore.state.res.projectsQuota.limit -
          projectsListStore.state.res.projectsQuota.used,
        0,
      )
    : ('unlimited' as const);

  // Paid users don't see the upgrade banner
  if (remainingProjects === 'unlimited' && trialDaysRemaining == null) {
    return null;
  }

  const humanPlanName = getHumanPlanName(planName);
  const showRemainingProjects =
    trialDaysRemaining == null || remainingProjects === 0;
  return (
    <>
      <Banner
        variant="white"
        icon={
          <Icon
            icon={
              showRemainingProjects
                ? IconProjectCreateGradient
                : IconClockGradient
            }
            color="colorPurple"
          />
        }
        actions={
          showRemainingProjects ? (
            <Button
              variant="primary"
              label="Upgrade to Pro"
              onClick={openUpgradeDialog}
              gradient
            />
          ) : (
            <Button
              variant="secondary"
              label="Manage account"
              href={manageAccountRouteToUrl({ path: 'manage-account/plan' })}
              onClick={navigateInternal}
            />
          )
        }
      >
        <Inline spacing={8}>
          {remainingProjects !== 'unlimited' && showRemainingProjects ? (
            <TextSmall>
              You have {remainingProjects}{' '}
              {pluralise(remainingProjects, 'project')} left
            </TextSmall>
          ) : (
            <TextSmall>Relume {humanPlanName}</TextSmall>
          )}
          {trialDaysRemaining == null ? (
            <TextSmallLight>Get Pro for unlimited access</TextSmallLight>
          ) : (
            <TextSmallLight>
              {trialDaysRemaining} {pluralise(trialDaysRemaining, 'day')}{' '}
              remaining of {humanPlanName} trial
            </TextSmallLight>
          )}
        </Inline>
      </Banner>
      {upgradeDialogOpen && (
        <ProFeatureGateDialog
          analytics={analytics}
          currentPlan={me.planName}
          initialFeatureKey="projects"
          onClose={closeUpgradeDialog}
        />
      )}
    </>
  );
});
